import React from 'react';
import { useQuery } from 'react-query';
import { 
  HiOutlineOfficeBuilding,
  HiOutlineClipboardList,
  HiOutlineDocumentText,
  HiOutlineUsers,
  HiOutlineTrendingUp
} from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { DashboardStats } from '../types';

interface StatCard {
  name: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
}

interface QuickActionCardProps {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const Dashboard: React.FC = () => {
  const { getUserStats } = useAuth();

  const { data: stats, isLoading, error } = useQuery<DashboardStats>(
    'userStats',
    getUserStats,
    {
      refetchOnWindowFocus: false,
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-error-50 p-4">
        <div className="text-sm text-error-700">
          Failed to load dashboard data. Please try again.
        </div>
      </div>
    );
  }

  const statCards: StatCard[] = [
    {
      name: 'Total Companies',
      value: stats?.totalCompanies || 0,
      icon: HiOutlineOfficeBuilding,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
    },
    {
      name: 'Total Applications',
      value: stats?.totalApplications || 0,
      icon: HiOutlineClipboardList,
      color: 'text-success-600',
      bgColor: 'bg-success-100',
    },
    {
      name: 'Total Resumes',
      value: stats?.totalResumes || 0,
      icon: HiOutlineDocumentText,
      color: 'text-warning-600',
      bgColor: 'bg-warning-100',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
        <p className="mt-1 text-sm text-secondary-600">
          Welcome back! Here's an overview of your job search progress.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card">
              <div className="card-content">
                <div className="flex items-center mt-4">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-secondary-600">
                      {stat.name}
                    </p>
                    <p className="text-2xl font-bold text-secondary-900">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Quick Actions</h3>
          <p className="card-description">
            Jump into your most common tasks
          </p>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <QuickActionCard
              title="Add Company"
              description="Track a new company"
              href="/companies"
              icon={HiOutlineOfficeBuilding}
            />
            <QuickActionCard
              title="New Application"
              description="Record a job application"
              href="/applications"
              icon={HiOutlineClipboardList}
            />
            <QuickActionCard
              title="Upload Resume"
              description="Add a new resume"
              href="/resumes"
              icon={HiOutlineDocumentText}
            />
            <QuickActionCard
              title="Add Contact"
              description="Network with professionals"
              href="/contacts"
              icon={HiOutlineUsers}
            />
          </div>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Activity</h3>
          <p className="card-description">
            Your latest job search activities
          </p>
        </div>
        <div className="card-content">
          <div className="text-center py-12">
            <HiOutlineTrendingUp className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">
              No recent activity
            </h3>
            <p className="mt-1 text-sm text-secondary-500">
              Start tracking your job applications to see activity here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard: React.FC<QuickActionCardProps> = ({ title, description, href, icon: Icon }) => {
  return (
    <a
      href={href}
      className="block p-4 border border-secondary-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all duration-200 group"
    >
      <div className="flex items-center space-x-3">
        <div className="bg-primary-100 p-2 rounded-lg group-hover:bg-primary-200 transition-colors">
          <Icon className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h4 className="text-sm font-medium text-secondary-900">
            {title}
          </h4>
          <p className="text-sm text-secondary-500">
            {description}
          </p>
        </div>
      </div>
    </a>
  );
};

export default Dashboard; 